import { quatConjugate, quatMul, quatRotate, quatYaw, wrap, type Quat, type Vec3 } from "../math";

const NORMALS: Vec3[] = [
  [1, 0, 0],
  [-1, 0, 0],
  [0, 1, 0],
  [0, -1, 0],
  [0, 0, 1],
  [0, 0, -1],
];

export interface CubeRead {
  ok: boolean;
  colorOk: boolean;
  color: number;
  pos: Vec3;
  phi: number;
  hint: number;
}

export const EMPTY_READ: CubeRead = {
  ok: false,
  colorOk: false,
  color: -1,
  pos: [0, 0, 0],
  phi: 0,
  hint: 0,
};

function foldedSpin(relative: Quat): number {
  const axes: Vec3[] = [
    quatRotate(relative, [1, 0, 0]),
    quatRotate(relative, [0, 1, 0]),
    quatRotate(relative, [0, 0, 1]),
  ];
  let axis = axes[0] ?? [1, 0, 0];
  for (const candidate of axes) {
    if (Math.abs(candidate[2]) < Math.abs(axis[2])) axis = candidate;
  }
  const angle = Math.atan2(axis[1], axis[0]);
  return ((angle + Math.PI / 4) % (Math.PI / 2) + Math.PI / 2) % (Math.PI / 2) - Math.PI / 4;
}

/** Exact geometric twin of the demo's cube read, isolated behind one seam. */
export function readCube(
  robotPos: ArrayLike<number>,
  robotQuat: ArrayLike<number>,
  cubePos: ArrayLike<number>,
  cubeQuat: ArrayLike<number>,
): CubeRead {
  let face = 0;
  let up = -Infinity;
  for (const [index, normal] of NORMALS.entries()) {
    const z = quatRotate(cubeQuat, normal)[2];
    if (z > up) {
      up = z;
      face = index;
    }
  }

  const yaw = quatYaw(robotQuat);
  const delta: Vec3 = [
    (cubePos[0] ?? 0) - (robotPos[0] ?? 0),
    (cubePos[1] ?? 0) - (robotPos[1] ?? 0),
    (cubePos[2] ?? 0) - (robotPos[2] ?? 0),
  ];
  const pos = quatRotate([Math.cos(-yaw / 2), 0, 0, Math.sin(-yaw / 2)], delta);
  const relative = quatMul(quatConjugate(robotQuat), cubeQuat);
  const stable = up >= 0.9;
  return {
    ok: stable,
    colorOk: stable,
    color: face,
    pos,
    phi: foldedSpin(relative),
    hint: wrap(Math.atan2(pos[1], pos[0])),
  };
}
